import type { Identifier } from '@studio/models';
import type { Error as PbError } from '@tryiris-ai/common.protojs/api/v1/common';
import { toNumber } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { StatusCode } from 'status-code-enum';
import { DefaultErrorStatus } from './constants.js';
import type { ApiResponse, Base64Id, ByteId, OmitResponseProps } from './types.js';
import { ApiError } from './types.js';
// W12 (Track 4a) — observability emitters. See logger/observability.ts for
// the user story (Shopify-incident grep target). Per `stay-in-scope`
// (ai.knowledge/global) we add a SHAPE-only log line at the central API
// boundary; diagnostic-path full-body logging happens at the diagnostic
// endpoint call sites which are NOT touched by the central wrapper.
import {
  isDiagnosticEndpoint,
  logApiResponse,
  logApiResponseShape
} from '#~/logger/observability.js';

/**
 * Convert a protobuf error record to a native JavaScript `StudioError`.
 *
 * @param error - Error to be wrapped.
 * @returns ApiError.
 */
export const toApiError = (error: PbError): ApiError => {
  const { code, message } = error;

  return new ApiError(message, { cause: error, code });
};

/**
 * Convert a Device ID byte array to a base64 string.
 *
 * @param id - Device identifier.
 * @returns Device ID as a Hex string.
 */
export const toBase64FromId = (id?: ByteId): T_Maybe<Base64Id> => {
  if (!id || id.length === 0) {
    return undefined;
  }

  let binary = '';

  const bytes = new Uint8Array(id);
  const length = bytes.byteLength;

  for (let ii = 0; ii < length; ii++) {
    // eslint-disable-next-line unicorn/prefer-code-point
    binary += String.fromCharCode(bytes[ii]!);
  }

  return <Base64Id>window.btoa(binary);
};

/**
 * Convert a byte array to a hex id.
 *
 * @param id - Device identifier.
 * @returns Device ID as a hex string.
 */
export const toHexFromId = <T extends string = Identifier>(id?: ByteId): T_Maybe<T> => {
  if (!id?.length) {
    return undefined;
  }

  return <T>[...new Uint8Array(id)].map((x) => x.toString(16).padStart(2, '0')).join('');
};

/**
 * Convert a Base64 Device ID to a byte array.
 *
 * @param id - Device identifier.
 * @returns Device ID as a byte array.
 */
export const toIdFromBase64 = (id?: string): T_Maybe<ByteId> =>
  id ? new TextEncoder().encode(window.atob(id)) : undefined;

/**
 * Convert a hex id to a byte array.
 *
 * @param id - Device identifier.
 * @returns Device ID as a byte array.
 */
export const toIdFromHex = (id?: string): T_Maybe<ByteId> => {
  if (!id) {
    return undefined;
  }

  const match = id
    .trim()
    .match(/.{1,2}/g)
    ?.map((byte) => Number.parseInt(byte, 16));

  if (!match) {
    return undefined;
  }

  return Uint8Array.from(match);
};

/**
 * Transforms a code-like value to an enumerated HTTP status code.
 *
 * @param value - Code value.
 * @returns HTTP status code (default `DefaultErrorStatus`).
 */
export const toStatusCode = (value: unknown): StatusCode => {
  const code = toNumber(value);

  return code && StatusCode[code] ? code : DefaultErrorStatus;
};

/**
 * Classify an error as a mailroom TRANSPORT failure (ENG-4176): the request
 * reached the mailroom but the response body could not be read (connection
 * reset, proxy drop). These are transient network blips, not product defects —
 * cosmetic fire-and-forget callers (e.g. the agent-icon model-config fetch)
 * may degrade quietly instead of toasting.
 *
 * Walks the `cause` chain because `wrapApiCall` re-wraps every rejection as an
 * `ApiError` with the original error on `.cause`. Matches on the stable
 * `error.name` discriminant (mailroom-helper's typed-error contract) rather
 * than `instanceof`, which is unreliable across bundle boundaries.
 * Per reference-rules-in-comments.md.
 *
 * @param error - The thrown error to classify.
 * @returns true iff a MailroomTransportError is anywhere on the cause chain.
 */
export const isMailroomTransportFailure = (error: unknown): boolean => {
  // Bounded walk: cause chains are short; the cap guards a pathological cycle.
  let current: unknown = error;

  for (let depth = 0; depth < 8 && current instanceof Error; depth++) {
    if (current.name === 'MailroomTransportError') {
      return true;
    }

    current = current.cause;
  }

  return false;
};

/**
 * Wrap an asynchronous API function call in a try/catch block. Throws an `ApiError` with the
 * specified `message` if the function rejects or returns an error. The `Error.cause` property will
 * be set to the original error.
 *
 * @param fn - Function to be invoked.
 * @param errorMessage - Error message.
 * @returns API result.
 * @throws An error if the function `fn` throws an error.
 */
export const wrapApiCall = async <T extends ApiResponse>(
  fn: () => Promise<T>,
  errorMessage?: string,
  // W12: optional endpoint hint — when supplied, the wrapper emits an
  // api.response observability line at the central boundary. We make it
  // optional (default undefined) so existing call sites keep working
  // unchanged per `stay-in-scope` (ai.knowledge/global) — only the
  // diagnostic-path callers we're targeting opt in.
  endpoint?: string
): Promise<OmitResponseProps<T>> => {
  // W12: capture start time for elapsed_ms reporting. Cheap (single
  // perf.now() / Date.now() — negligible vs the API roundtrip).
  const startedAt = Date.now();
  try {
    const x = await fn();
    const { $type: _, error, ...rest } = x;

    if (error && error.code >= 400) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw error;
    }

    // W12 — observability emit. Per logger/observability.ts the helper
    // routes to full-body logging only for the four diagnostic endpoint
    // names; everything else (or no endpoint hint) lands in shape-only.
    if (endpoint) {
      const elapsedMs = Date.now() - startedAt;
      // The W3 redacted logger applies redaction at the sink, so even
      // diagnostic full-body lines are scrubbed of secrets.
      if (isDiagnosticEndpoint(endpoint)) {
        logApiResponse(endpoint, 200, elapsedMs, rest);
      } else {
        logApiResponseShape(endpoint, 200, elapsedMs, rest);
      }
    }

    return rest;
  } catch (cause: unknown) {
    // W12 — observability emit on failure. We use shape-only here (the
    // error path) to avoid logging error envelopes that may contain
    // verbose stack traces with paths. status is best-effort.
    if (endpoint) {
      const elapsedMs = Date.now() - startedAt;
      const statusGuess =
        cause && typeof cause === 'object' && 'code' in cause
          ? toStatusCode((cause as { code: unknown }).code)
          : 500;
      logApiResponseShape(endpoint, statusGuess, elapsedMs, cause);
    }
    if (cause instanceof Error) {
      throw new ApiError(errorMessage ?? cause.message, { cause });
    }

    const code =
      cause && typeof cause === 'object' && 'code' in cause ? toStatusCode(cause.code) : undefined;

    // The protobuf `Error` envelope thrown above is a plain object, not an Error
    // instance, so it misses the `cause.message` fallback in the branch above —
    // without this the server's explanation is dropped and every coded failure
    // renders as the ApiError default from api/types.ts.
    const causeMessage =
      cause && typeof cause === 'object' && 'message' in cause && typeof cause.message === 'string'
        ? cause.message
        : undefined;

    throw new ApiError(errorMessage ?? causeMessage, { cause, code });
  }
};
